import { pickDefaultLandingForRoles, type SessionValue } from './session-provider';
import { normalizeReturnToPath } from './return-path';
import { publicOrganizationReturn } from './public-organization-routing';
export { normalizeReturnToPath } from './return-path';

export function buildSignInPath(returnTo?: string): string {
  const safe = normalizeReturnToPath(returnTo);
  if (!safe) return '/signin';
  const params = new URLSearchParams({ returnTo: safe });
  return `/signin?${params.toString()}`;
}

export function resolvePostSignInPath(
  roles: ReadonlyArray<string>,
  returnTo?: string | null,
): string {
  return normalizeReturnToPath(returnTo) ?? pickDefaultLandingForRoles(roles);
}

/** Keep a deep link through organization selection, without nested account redirects. */
export function buildOrganizationAccountPath(returnTo?: string | null): string {
  const safe = normalizeReturnToPath(returnTo);
  if (!safe) return '/account';
  if (new URL(safe, 'https://event.invalid').pathname === '/account') return safe;
  return `/account?${new URLSearchParams({ returnTo: safe }).toString()}`;
}

/** A tenant is usable only after the server has resolved its active membership. */
export function resolvePostSignInPathForSession(
  session: Pick<SessionValue, 'managedMembership' | 'tenant' | 'user'>,
  returnTo?: string | null,
): string {
  // An organization-specific entry verifies its own membership. Sending it
  // through the generic picker could select an unrelated sole membership.
  const organizationReturn = publicOrganizationReturn(returnTo);
  if (organizationReturn) return organizationReturn.path;
  if (session.managedMembership && !session.tenant) return buildOrganizationAccountPath(returnTo);
  return resolvePostSignInPath(session.user?.roles ?? [], returnTo);
}

export function appendReturnToToUrl(
  url: string,
  returnTo?: string | null,
): string {
  const safe = normalizeReturnToPath(returnTo);
  if (!safe) return url;
  const parsed = new URL(url, window.location.origin);
  parsed.searchParams.set('returnTo', safe);
  return parsed.toString();
}

export function extractAuthCallbackParams(location: {
  readonly search: string;
  readonly hash: string;
}): {
  readonly accessToken: string | undefined;
  readonly returnTo: string | undefined;
} {
  const searchParams = new URLSearchParams(location.search);
  const hashParams = new URLSearchParams(location.hash.replace(/^#/, ''));

  const accessToken =
    searchParams.get('access_token') ??
    searchParams.get('token') ??
    hashParams.get('access_token') ??
    hashParams.get('token') ??
    undefined;

  const returnTo =
    searchParams.get('returnTo') ??
    hashParams.get('returnTo') ??
    undefined;

  return {
    accessToken: accessToken ?? undefined,
    returnTo: normalizeReturnToPath(returnTo),
  };
}
